/* eslint-disable no-nested-ternary */
import SliderNew from '@components/slider-new'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Container from '@material-ui/core/Container'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import LinearProgress from '@material-ui/core/LinearProgress'
import NoSsr from '@material-ui/core/NoSsr'
import CoachCardOptimized from '@components/coach-card-optimized'
import ButtonCta from '@components/atoms/button-cta'
import {
  IconMarkerNew,
  IconMarkerOutlinePlusFill,
  IconClose,
  IconConnect,
} from '@components/atoms/icons'
import IconButton from '@material-ui/core/IconButton'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { geocodeZip } from '@lib/geo-api'
import { COACH_RAIL_SLIDER_CONFIG } from '@lib/pga-coach/coach-rail'
import useStyles from './coaches-search-new.styles'
import ZipSearch from './zip-search'

function CoachesSearchNew(props) {
  const {
    headline,
    ctaText,
    ctaLink,
    coaches,
    loading,
    open,
    disable,
    buttonMessage,
    displayZip,
    onSubmit,
    onCurrentLocationClick,
    onOpenModal,
    onCloseModal,
    zipError,
    onClearZipErrors,
    noResults,
    onNoResults,
    source,
  } = props

  const [formZip, setFormZip] = useState('')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const styles = useStyles()

  const searchForZipCode = async (zipCode) => {
    if (!zipCode || zipCode.length < 5) { return }

    const result = await geocodeZip(zipCode)
    if (result) {
      const { lat, lng } = result
      window.datafields = {
        postalCode: zipCode,
        findacoach_geo_location: {
          lat,
          lon: lng,
        },
      }
    }
  }

  const handleRequestCoach = async () => {
    onCloseModal()
    await searchForZipCode(formZip)
  }

  return (
    <div className={styles.root}>
      <Dialog
        fullScreen={isMobile}
        open={open}
        onClose={onCloseModal}
        aria-modal="true"
        className={styles.modalContainer}
      >
        <Box className={styles.modal} data-testid="modal">
          <IconButton
            data-testid="close-modal"
            className={styles.closeIconButton}
            title="close"
            onClick={onCloseModal}
          >
            <IconClose className={styles.closeIcon} />
          </IconButton>
          {!noResults ? (
            <Box className={styles.content}>
              <Box className={styles.topContent}>
                <IconMarkerOutlinePlusFill className={styles.icon} />
                <Typography variant="h3" className={styles.modalTitle}>Set Your Location</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={onCurrentLocationClick}
                  disabled={disable}
                  data-testid="use-location"
                >
                  {buttonMessage}
                </Button>
              </Box>
              <Box className={styles.bottomContent}>
                <Typography variant="body1" className={styles.zipSubtitle}>
                  Or enter your zip code to search for coaches in your area.
                </Typography>
                <ZipSearch
                  isMobile={isMobile}
                  handleClose={onCloseModal}
                  formZip={formZip}
                  zipError={zipError}
                  handleSubmit={() => onSubmit(formZip)}
                  disable={disable}
                  onClearErrors={onClearZipErrors}
                  onZipChange={setFormZip}
                />
              </Box>
            </Box>
          ) : (
            <div className={styles.content}>
              <Box className={styles.topContent}>
                <IconConnect className={styles.icon} />
                <Typography variant="h3" className={styles.modalTitle}>We are working on it!</Typography>
                <Typography variant="body1" className={styles.noResultsSubtitle}>
                  Sorry, there are no available coaches in your area. Would you
                  like to
                  <button
                    onClick={() => onNoResults(false)}
                    type="button"
                    className={styles.diffZip}
                  >
                    try a different zip code
                  </button>
                  or view all coaches?
                </Typography>
              </Box>
              <div className={styles.noResultsCtas}>
                <Button
                  className="request-coach-button"
                  variant="contained"
                  color="primary"
                  onClick={handleRequestCoach}
                >
                  Request a coach in my area
                </Button>
                <ButtonCta
                  className={styles.coachesCta}
                  variant="text"
                  arrow
                  href={ctaLink}
                >
                  {ctaText}
                </ButtonCta>
              </div>

            </div>
          )}
        </Box>
      </Dialog>
      <Container>
        <div className={styles.headlineContainer}>
          <NoSsr>
            <div className={styles.headlineLeft}>
              <Typography
                variant="h4"
                variantMapping={{ h4: 'h2' }}
              >
                {headline}
              </Typography>
              <div className={styles.subtitleContainer}>
                <div className={styles.resultsText} aria-live="polite">
                  <IconMarkerNew
                    className={styles.iconMarker}
                    viewBox="0 -2 8 17"
                  />
                  {displayZip && (
                    <Typography
                      variant="subtitle1"
                      className={styles.displayZip}
                      aria-label={`showing results near ${displayZip}`}
                    >
                      Results Near {displayZip}.
                    </Typography>
                  )}
                </div>
                {displayZip && <div className={styles.divider} />}
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => {
                    setFormZip('')
                    onOpenModal()
                  }}
                  data-testid="change-location"
                  className={styles.changeLocationBtn}
                >
                  Change Location
                </Button>
              </div>
            </div>
          </NoSsr>
          <Box className={styles.cta1}>
            <ButtonCta
              className={styles.involvementCta}
              variant="text"
              color="primary"
              arrow
              href={ctaLink}
            >
              {ctaText}
            </ButtonCta>
          </Box>
        </div>
        {loading ? (
          <LinearProgress color="secondary" />
        ) : coaches.length > 0 ? (
          <div data-testid="coaches-slider" className={styles.sliderContainer}>
            <SliderNew className={styles.slider} config={COACH_RAIL_SLIDER_CONFIG}>
              {coaches.map((info) => (
                <CoachCardOptimized
                  coachInfo={info}
                  source={source}
                  key={info.objectID}
                  hideSaveToggle
                />
              ))}
            </SliderNew>
            <Box className={styles.cta2}>
              <ButtonCta
                className={styles.involvementCta}
                variant="text"
                color="primary"
                arrow
                href={ctaLink}
              >
                {ctaText}
              </ButtonCta>
            </Box>
          </div>
        ) : (
          <div
            data-testid="no-results-page"
            className={styles.noResultsContainer}
          >
            <Typography variant="h5">
              Sorry, there are no results in your area.
            </Typography>
            <Typography variant="body1" className={styles.noResultsBody}>
              You can try a different zip code or view all coaches.
            </Typography>
            <div className={styles.noResultsCtas}>
              <ButtonCta
                className={styles.involvementCta}
                variant="contained"
                color="primary"
                href={ctaLink}
              >
                {ctaText}
              </ButtonCta>
            </div>
          </div>
        )}
      </Container>
    </div>
  )
}

CoachesSearchNew.propTypes = {
  headline: PropTypes.string,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  coaches: PropTypes.array,
  loading: PropTypes.bool,
  open: PropTypes.bool,
  disable: PropTypes.bool,
  buttonMessage: PropTypes.string,
  onSubmit: PropTypes.func,
  onCurrentLocationClick: PropTypes.func,
  onOpenModal: PropTypes.func,
  onCloseModal: PropTypes.func,
  displayZip: PropTypes.string,
  zipError: PropTypes.string,
  onClearZipErrors: PropTypes.func,
  noResults: PropTypes.bool,
  onNoResults: PropTypes.func,
  source: PropTypes.string,
}

export default CoachesSearchNew
